import { Fragment, useMemo, useState } from 'react';
import { Copy, Check, Download, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Select } from '../ui/select-custom';
import { Card, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { TableShell, Th, Td, EmptyState } from './investment-tables';
import { downloadCsv } from '../../lib/csv';
import { formatCurrency } from '../../lib/format';
import {
  buildBensEDireitos,
  buildEarningsTables,
  computeMonthlyTaxes,
} from '../../lib/investments';
import type { PortfolioInput } from '../../lib/investments';
import {
  DARF_CODE,
  DARF_MINIMUM,
  IRPF_CODES,
  STOCK_MONTHLY_EXEMPTION,
  investmentTypes,
} from '../../types';
import type { InvestmentType } from '../../types';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function brl(value: number): string {
  return `R$ ${formatCurrency(value)}`;
}

function CopyButton({ text, label = 'Copiar' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard bloqueado (contexto não seguro): o texto segue visível na tela.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={label}
      className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {copied ? <Check className="size-3.5 text-green-600" /> : <Copy className="size-3.5" />}
    </button>
  );
}

interface SectionProps {
  title: string;
  description?: string;
  code?: string;
  onExport?: () => void;
  children: React.ReactNode;
}

function Section({ title, description, code, onExport, children }: SectionProps) {
  return (
    <Card className="gap-4">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-sm">{title}</CardTitle>
              {code && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {code}
                </span>
              )}
            </div>
            {description && <CardDescription className="text-xs">{description}</CardDescription>}
          </div>
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="size-3.5" />
              CSV
            </Button>
          )}
        </div>
      </CardHeader>
      {children}
    </Card>
  );
}

export interface IrpfTablesProps {
  portfolio: PortfolioInput;
}

export function IrpfTables({ portfolio }: IrpfTablesProps) {
  const currentYear = new Date().getFullYear();

  // Anos com movimento, do mais recente para o mais antigo.
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    for (const trade of portfolio.trades) years.add(trade.date.getFullYear());
    for (const earning of portfolio.earnings) years.add(earning.paymentDate.getFullYear());
    if (years.size === 0) years.add(currentYear);
    // O ano-base da declaração é o anterior, então ele costuma ser o padrão.
    return [...years].sort((a, b) => b - a);
  }, [portfolio.trades, portfolio.earnings, currentYear]);

  const defaultYear = availableYears.includes(currentYear - 1)
    ? currentYear - 1
    : availableYears[0]!;
  const [year, setYear] = useState(defaultYear);

  const bens = useMemo(
    () => buildBensEDireitos(portfolio, year, IRPF_CODES),
    [portfolio, year]
  );

  const earningsTables = useMemo(
    () => buildEarningsTables(portfolio, year, IRPF_CODES),
    [portfolio, year]
  );

  const monthlyTaxes = useMemo(() => computeMonthlyTaxes(portfolio), [portfolio]);
  const yearMonths = monthlyTaxes.filter((m) => m.year === year);

  const totalDarf = yearMonths.reduce((sum, m) => sum + m.darfToPay, 0);
  const monthsWithDarf = yearMonths.filter((m) => m.darfToPay > 0);
  const lastMonth = monthlyTaxes.filter((m) => m.year <= year).at(-1);
  /** Imposto apurado que não chegou ao mínimo e segue acumulando. */
  const pendingDarf = yearMonths.at(-1)?.darfCarriedForward ?? 0;

  const exportBens = () => {
    downloadCsv(
      ['Grupo', 'Código', 'Papel', 'CNPJ', `Qtd 31/12/${year}`, `Situação 31/12/${year - 1}`, `Situação 31/12/${year}`, 'Discriminação'],
      bens.map((row) => [
        row.grupo,
        row.codigo,
        row.asset.ticker,
        row.cnpj,
        String(row.quantityCurrent),
        row.costPrevious.toFixed(2).replace('.', ','),
        row.costCurrent.toFixed(2).replace('.', ','),
        row.discriminacao,
      ]),
      `irpf-${year}-bens-e-direitos.csv`
    );
  };

  const exportRendimentos = () => {
    const rows = [
      ...earningsTables.dividendos.map((r) => ['Isento', r.codigo, r.asset.ticker, r.cnpj, r.grossValue, 0]),
      ...earningsTables.rendimentosFii.map((r) => ['Isento', r.codigo, r.asset.ticker, r.cnpj, r.grossValue, 0]),
      ...earningsTables.jcp.map((r) => ['Exclusiva', r.codigo, r.asset.ticker, r.cnpj, r.grossValue, r.irrfWithheld]),
    ];
    downloadCsv(
      ['Ficha', 'Código', 'Papel', 'CNPJ', 'Valor bruto', 'IRRF'],
      rows.map((r) => [
        String(r[0]),
        String(r[1]),
        String(r[2]),
        String(r[3]),
        Number(r[4]).toFixed(2).replace('.', ','),
        Number(r[5]).toFixed(2).replace('.', ','),
      ]),
      `irpf-${year}-rendimentos.csv`
    );
  };

  const exportRendaVariavel = () => {
    downloadCsv(
      ['Mês', 'Tipo', 'Vendas', 'Resultado', 'Isento', 'Prejuízo compensado', 'Base', 'Imposto', 'IRRF', 'DARF a pagar'],
      yearMonths.flatMap((month) =>
        month.buckets
          .filter((bucket) => bucket.sales > 0 || bucket.result !== 0)
          .map((bucket) => [
            MONTH_NAMES[month.month - 1]!,
            investmentTypes[bucket.type].shortLabel,
            bucket.sales.toFixed(2).replace('.', ','),
            bucket.result.toFixed(2).replace('.', ','),
            bucket.exempt ? 'Sim' : 'Não',
            bucket.lossUsed.toFixed(2).replace('.', ','),
            bucket.taxableBase.toFixed(2).replace('.', ','),
            bucket.tax.toFixed(2).replace('.', ','),
            month.irrfWithheld.toFixed(2).replace('.', ','),
            month.darfToPay.toFixed(2).replace('.', ','),
          ])
      ),
      `irpf-${year}-renda-variavel.csv`
    );
  };

  const hasData =
    bens.length > 0 ||
    earningsTables.totals.received > 0 ||
    yearMonths.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Seletor de ano-base */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-subtle">Ano-base</span>
          <Select
            value={String(year)}
            onChange={(value) => setYear(Number(value))}
            options={availableYears.map((y) => ({ value: String(y), label: String(y) }))}
          />
          <span className="text-xs text-muted-foreground">
            (declaração entregue em {year + 1})
          </span>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        <span>
          Os códigos das fichas mudam entre exercícios. Confira os códigos no programa
          da Receita do ano antes de transcrever. Os valores aqui saem do seu histórico
          de operações — confira contra as notas de corretagem e o informe de
          rendimentos da corretora.
        </span>
      </div>

      {!hasData && (
        <Card>
          <EmptyState>Nenhum dado de investimento em {year}.</EmptyState>
        </Card>
      )}

      {/* ---------- BENS E DIREITOS ---------- */}
      {bens.length > 0 && (
        <Section
          title="Bens e Direitos"
          description={`Posição em 31/12 pelo custo de aquisição — nunca pelo valor de mercado.`}
          onExport={exportBens}
        >
          <TableShell>
            <thead>
              <tr>
                <Th>Grupo/Cód.</Th>
                <Th>Papel</Th>
                <Th>CNPJ</Th>
                <Th align="right">Qtd 31/12</Th>
                <Th align="right">Situação em 31/12/{year - 1}</Th>
                <Th align="right">Situação em 31/12/{year}</Th>
              </tr>
            </thead>
            <tbody>
              {bens.map((row) => (
                <Fragment key={row.asset.id}>
                  <tr>
                    <Td className="border-b-0">
                      <span className="font-mono text-xs">
                        {row.grupo} / {row.codigo}
                      </span>
                    </Td>
                    <Td className="border-b-0 font-medium text-foreground">
                      {row.asset.ticker}
                    </Td>
                    <Td className="border-b-0">
                      {row.cnpj ? (
                        <span className="font-mono text-xs">{row.cnpj}</span>
                      ) : (
                        <span className="text-xs text-amber-600">faltando</span>
                      )}
                    </Td>
                    <Td align="right" className="border-b-0">
                      {row.quantityCurrent}
                    </Td>
                    <Td align="right" className="border-b-0">
                      {brl(row.costPrevious)}
                    </Td>
                    <Td align="right" className="border-b-0 font-medium">
                      {brl(row.costCurrent)}
                    </Td>
                  </tr>
                  <tr>
                    <Td colSpan={6} className="whitespace-normal pt-0">
                      <div className="flex items-start gap-1 rounded-md bg-muted px-2 py-1.5">
                        <span className="text-xs text-foreground-subtle">
                          {row.discriminacao}
                        </span>
                        <CopyButton text={row.discriminacao} label="Copiar discriminação" />
                      </div>
                    </Td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-medium">
                <Td colSpan={4}>Total</Td>
                <Td align="right">
                  {brl(bens.reduce((sum, r) => sum + r.costPrevious, 0))}
                </Td>
                <Td align="right">
                  {brl(bens.reduce((sum, r) => sum + r.costCurrent, 0))}
                </Td>
              </tr>
            </tfoot>
          </TableShell>
        </Section>
      )}

      {/* ---------- RENDIMENTOS ISENTOS ---------- */}
      {(earningsTables.dividendos.length > 0 || earningsTables.rendimentosFii.length > 0) && (
        <Section
          title="Rendimentos Isentos e Não Tributáveis"
          description="Dividendos de ações e rendimentos de FII, agrupados por fonte pagadora."
          onExport={exportRendimentos}
        >
          <TableShell>
            <thead>
              <tr>
                <Th>Código</Th>
                <Th>Papel</Th>
                <Th>CNPJ da fonte pagadora</Th>
                <Th>Tipo</Th>
                <Th align="right">Valor</Th>
              </tr>
            </thead>
            <tbody>
              {[...earningsTables.dividendos, ...earningsTables.rendimentosFii].map((row) => (
                <tr key={`${row.codigo}-${row.asset.id}`}>
                  <Td>
                    <span className="font-mono text-xs">{row.codigo}</span>
                  </Td>
                  <Td className="font-medium text-foreground">{row.asset.ticker}</Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      {row.cnpj ? (
                        <>
                          <span className="font-mono text-xs">{row.cnpj}</span>
                          <CopyButton text={row.cnpj} label="Copiar CNPJ" />
                        </>
                      ) : (
                        <span className="text-xs text-amber-600">faltando</span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <span className="text-xs text-foreground-subtle">{row.label}</span>
                  </Td>
                  <Td align="right" className="font-medium">
                    {brl(row.grossValue)}
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-medium">
                <Td colSpan={4}>Total isento</Td>
                <Td align="right">
                  {brl(earningsTables.totals.dividendos + earningsTables.totals.rendimentosFii)}
                </Td>
              </tr>
            </tfoot>
          </TableShell>
        </Section>
      )}

      {/* ---------- TRIBUTAÇÃO EXCLUSIVA (JCP) ---------- */}
      {earningsTables.jcp.length > 0 && (
        <Section
          title="Rendimentos Sujeitos à Tributação Exclusiva na Fonte"
          code={`Código ${IRPF_CODES.exclusivaJcp.codigo}`}
          description="JCP: o imposto já foi retido na fonte, você só informa."
        >
          <TableShell>
            <thead>
              <tr>
                <Th>Papel</Th>
                <Th>CNPJ da fonte pagadora</Th>
                <Th align="right">Valor bruto</Th>
                <Th align="right">IRRF (15%)</Th>
                <Th align="right">Líquido</Th>
              </tr>
            </thead>
            <tbody>
              {earningsTables.jcp.map((row) => (
                <tr key={row.asset.id}>
                  <Td className="font-medium text-foreground">{row.asset.ticker}</Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      {row.cnpj ? (
                        <>
                          <span className="font-mono text-xs">{row.cnpj}</span>
                          <CopyButton text={row.cnpj} label="Copiar CNPJ" />
                        </>
                      ) : (
                        <span className="text-xs text-amber-600">faltando</span>
                      )}
                    </div>
                  </Td>
                  <Td align="right">{brl(row.grossValue)}</Td>
                  <Td align="right" className="text-red-600">
                    -{brl(row.irrfWithheld)}
                  </Td>
                  <Td align="right" className="font-medium">
                    {brl(row.netValue)}
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-medium">
                <Td colSpan={2}>Total</Td>
                <Td align="right">{brl(earningsTables.totals.jcpGross)}</Td>
                <Td align="right" className="text-red-600">
                  -{brl(earningsTables.totals.jcpIrrf)}
                </Td>
                <Td align="right">{brl(earningsTables.totals.jcpNet)}</Td>
              </tr>
            </tfoot>
          </TableShell>
        </Section>
      )}

      {/* ---------- RENDA VARIÁVEL MÊS A MÊS ---------- */}
      {yearMonths.length > 0 && (
        <Section
          title="Renda Variável — apuração mês a mês"
          description={`Swing trade. Isenção de ${brl(STOCK_MONTHLY_EXEMPTION)} por mês em vendas de ações; FII não tem isenção.`}
          onExport={exportRendaVariavel}
        >
          <TableShell>
            <thead>
              <tr>
                <Th>Mês</Th>
                <Th>Tipo</Th>
                <Th align="right">Vendas</Th>
                <Th align="right">Resultado</Th>
                <Th align="right">Prejuízo compensado</Th>
                <Th align="right">Base</Th>
                <Th align="right">Imposto</Th>
                <Th align="right">Prejuízo a compensar</Th>
              </tr>
            </thead>
            <tbody>
              {yearMonths.flatMap((month) =>
                month.buckets
                  .filter((bucket) => bucket.sales > 0 || bucket.result !== 0)
                  .map((bucket) => (
                    <tr key={`${month.month}-${bucket.type}`}>
                      <Td>{MONTH_NAMES[month.month - 1]}</Td>
                      <Td>
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            className="size-2 shrink-0 rounded-sm"
                            style={{ backgroundColor: investmentTypes[bucket.type].color }}
                            aria-hidden
                          />
                          <span className="text-xs text-foreground-subtle">
                            {investmentTypes[bucket.type].shortLabel}
                          </span>
                        </span>
                      </Td>
                      <Td align="right">{brl(bucket.sales)}</Td>
                      <Td align="right">
                        <div className="flex flex-col items-end">
                          <span className={bucket.result >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {bucket.result >= 0 ? '+' : '-'}
                            {brl(Math.abs(bucket.result))}
                          </span>
                          {bucket.exempt && (
                            <span className="text-xs text-green-700">isento</span>
                          )}
                        </div>
                      </Td>
                      <Td align="right">
                        {bucket.lossUsed > 0 ? (
                          brl(bucket.lossUsed)
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </Td>
                      <Td align="right">{brl(bucket.taxableBase)}</Td>
                      <Td align="right" className="font-medium">
                        {bucket.tax > 0 ? (
                          brl(bucket.tax)
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </Td>
                      <Td align="right">
                        {bucket.lossCarryOut > 0 ? (
                          <span className="text-amber-700">{brl(bucket.lossCarryOut)}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </Td>
                    </tr>
                  ))
              )}
            </tbody>
          </TableShell>

          {/* DARF por mês */}
          <div className="flex flex-col gap-2 rounded-lg bg-muted p-3">
            <div className="flex items-center gap-2 text-xs font-medium text-foreground-subtle">
              DARF — código {DARF_CODE}
            </div>
            {monthsWithDarf.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nenhuma DARF devida em {year}.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {monthsWithDarf.map((month) => (
                  <div
                    key={month.month}
                    className="flex flex-wrap items-center justify-between gap-2 text-xs"
                  >
                    <span className="text-foreground-subtle">
                      {MONTH_NAMES[month.month - 1]}
                      {month.carriedFromPreviousMonths > 0 && (
                        <span className="text-muted-foreground">
                          {' '}
                          (inclui {brl(month.carriedFromPreviousMonths)} acumulado de meses
                          anteriores)
                        </span>
                      )}
                    </span>
                    <span className="font-semibold text-foreground">
                      {brl(month.darfToPay)}
                      <span className="ml-1 font-normal text-muted-foreground">
                        — vence no último dia útil de {MONTH_NAMES[month.month % 12]}
                      </span>
                    </span>
                  </div>
                ))}
                <div className="mt-1 flex items-center justify-between border-t border-border pt-1.5 text-xs font-semibold">
                  <span>Total recolhido no ano</span>
                  <span>{brl(totalDarf)}</span>
                </div>
              </div>
            )}

            {pendingDarf > 0 && (
              <p className="text-xs text-muted-foreground">
                {brl(pendingDarf)} de imposto apurado ficou abaixo do mínimo de{' '}
                {brl(DARF_MINIMUM)} e acumula para a próxima DARF, em vez de ser recolhido
                agora.
              </p>
            )}
          </div>

          {/* Prejuízo acumulado a carregar para o ano seguinte */}
          {lastMonth && lastMonth.buckets.some((b) => b.lossCarryOut > 0) && (
            <div className="flex items-start gap-2 rounded-lg border border-border px-3 py-2 text-xs">
              <Info className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
              <div className="flex flex-col gap-0.5">
                <span className="font-medium text-foreground">
                  Prejuízo a compensar no fim de {year}
                </span>
                {lastMonth.buckets
                  .filter((b) => b.lossCarryOut > 0)
                  .map((b) => (
                    <span key={b.type} className="text-muted-foreground">
                      {investmentTypes[b.type as InvestmentType].shortLabel}:{' '}
                      {brl(b.lossCarryOut)} — só compensa com lucro do mesmo tipo.
                    </span>
                  ))}
              </div>
            </div>
          )}

          {yearMonths.some((m) => m.nonDeductibleLoss > 0) && (
            <div className="flex items-start gap-2 rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              <span>
                {brl(yearMonths.reduce((sum, m) => sum + m.nonDeductibleLoss, 0))} de prejuízo
                foi apurado em meses isentos (vendas de ações até {brl(STOCK_MONTHLY_EXEMPTION)}).
                A legislação não permite compensar esse prejuízo.
              </span>
            </div>
          )}
        </Section>
      )}

      {/* ---------- REDUÇÕES DE CUSTO ---------- */}
      {earningsTables.reducoesDeCusto.length > 0 && (
        <Section
          title="Amortizações e restituições de capital"
          description="Não vão em ficha de rendimento: já estão abatidas do custo de aquisição acima."
        >
          <TableShell>
            <thead>
              <tr>
                <Th>Papel</Th>
                <Th>Tipo</Th>
                <Th align="right">Valor recebido</Th>
              </tr>
            </thead>
            <tbody>
              {earningsTables.reducoesDeCusto.map((row) => (
                <tr key={row.asset.id}>
                  <Td className="font-medium text-foreground">{row.asset.ticker}</Td>
                  <Td>
                    <span className="text-xs text-foreground-subtle">{row.label}</span>
                  </Td>
                  <Td align="right">{brl(row.grossValue)}</Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </Section>
      )}
    </div>
  );
}
